
import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut, 
  Auth,
  getAuth,
  initializeAuth,
  browserLocalPersistence, 
  browserPopupRedirectResolver,
  indexedDBLocalPersistence,
  connectAuthEmulator
} from "firebase/auth";
import {
  Firestore,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  updateDoc,
  deleteField
} from "firebase/firestore";
import { getStorage, FirebaseStorage } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator, httpsCallable, Functions } from "firebase/functions";
import { initializeAppCheck, ReCaptchaEnterpriseProvider, type AppCheck } from "firebase/app-check";
import { firebaseConfig, getConfigError, appConfig } from "./config";
import { logger } from "./utils/logger";
import { decideAppCheck } from "./utils/appCheckSetup";
import type { BidWalkQboEstimate, BidWalkEstimatePreviewResult, JobInvoicePreviewResult, JobQboInvoice, QboItemOption, QboApproval } from './types';

// GLOBAL VARIABLES
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let functions: Functions;
/** Null when App Check is off (emulators, staging) or failed to start. */
let appCheck: AppCheck | null = null;
// New export to track if initialization failed
export let initializationError: Error | null = null;

try {
  // Check for configuration errors first
  const configError = getConfigError();
  if (configError) {
    initializationError = configError;
    throw configError;
  }

  // 1. Get or Create App
  const existingApps = getApps();
  if (existingApps.length > 0) {
    app = existingApps[0];
  } else {
    app = initializeApp(firebaseConfig);
  }

  // 1b. App Check — before any other service, so their requests carry a token.
  // Never fatal: if it fails, only enforced services (Firebase AI Logic, once
  // switched on in the console) are affected; everything else is unenforced.
  const appCheckPlan = decideAppCheck({
    siteKey: appConfig.recaptchaSiteKey,
    isEmulator: appConfig.isDevelopment,
    hostname: typeof window !== 'undefined' ? window.location.hostname : '',
    debugToken: appConfig.appCheckDebugToken,
  });
  if (appCheckPlan.enabled) {
    try {
      if (appCheckPlan.debugToken !== null) {
        (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckPlan.debugToken;
      }
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(appCheckPlan.siteKey),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (err) {
      logger.warn('App Check could not start; AI features may be refused once enforced', err);
    }
  }

  // 2. Initialize Auth
  try {
    auth = getAuth(app);
  } catch {
    try {
      auth = initializeAuth(app, {
        persistence: [indexedDBLocalPersistence, browserLocalPersistence],
        popupRedirectResolver: browserPopupRedirectResolver,
      });
    } catch (initAuthError: any) {
      initializationError = initAuthError;
      // If both fail, try to get auth anyway (might work in some cases)
      try {
        auth = getAuth(app);
      } catch {
        // If all fails, create a dummy auth to satisfy TypeScript
        // The app will fail later if auth is not properly initialized
        auth = getAuth(app);
      }
    }
  }

  // Connect to Auth Emulator in development
  if (appConfig.isDevelopment) {
    try {
      // Check if emulator is already connected by trying to connect
      // This will throw if already connected, which we'll catch
      if (auth) {
        connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
        logger.info('✅ Connected to Firebase Auth Emulator');
      }
    } catch (err: any) {
      // Emulator already connected or not running - this is fine
      if (err.message?.includes('already') || err.message?.includes('Cannot call')) {
        // Already connected, that's fine
      } else {
        logger.warn('⚠️  Auth Emulator not available (running in dev mode without emulator)');
      }
    }
  }

  // 3. Initialize Firestore with cache settings
  // Note: Cross-Origin-Opener-Policy (COOP) warnings from Firebase are expected behavior
  // when using popup-based authentication. These warnings are informational and don't affect functionality.
  // They occur because Firebase needs to check window.closed for popup authentication flows.
  try {
    if (!appConfig.isDevelopment) {
      // Production: persistent IndexedDB cache so repeat visits load from local storage
      db = initializeFirestore(app, {
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
      });
    } else {
      // Development: Use initializeFirestore without cache (emulator doesn't need persistence)
      db = initializeFirestore(app, {
        cacheSizeBytes: 0
      });
    }
  } catch (initError: any) {
    // Firestore may already be initialized (e.g., hot reload, multiple imports)
    // Fall back to getFirestore which returns existing instance
    // This is the only valid fallback - if initializeFirestore fails because it's already initialized,
    // getFirestore will return the existing instance
    try {
      db = getFirestore(app);
      logger.warn('Firestore already initialized, using existing instance');
    } catch (getError: any) {
      // Both initializeFirestore and getFirestore failed
      // This indicates a real configuration or initialization problem
      initializationError = getError || initError;
      logger.error('Firestore initialization failed completely:', initializationError);
      // As a last resort, try getFirestore one more time - it should always return an instance
      // even if not properly configured, to satisfy TypeScript and prevent runtime errors
      db = getFirestore(app);
    }
  }
  
  // Connect to Firestore Emulator in development
  if (appConfig.isDevelopment) {
    try {
      connectFirestoreEmulator(db, 'localhost', 8081);
      logger.info('✅ Connected to Firebase Firestore Emulator');
    } catch (err: any) {
      // Emulator already connected or not running - this is fine
      if (err.message?.includes('already') || err.message?.includes('Cannot call')) {
        // Already connected, that's fine
      } else {
        logger.warn('⚠️  Firestore Emulator not available (running in dev mode without emulator)');
      }
    }
  }

  // 4. Initialize Storage
  storage = getStorage(app);

  // 5. Initialize Functions
  functions = getFunctions(app);
  if (appConfig.isDevelopment) {
    try {
      connectFunctionsEmulator(functions, 'localhost', 5001);
      logger.info('✅ Connected to Firebase Functions Emulator');
    } catch (err: any) {
      if (!err.message?.includes('already') && !err.message?.includes('Cannot call')) {
        logger.warn('⚠️  Functions Emulator not available');
      }
    }
  }

} catch (error: any) {
  initializationError = error;
}

// CONFIGURATION
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// AUTH ACTIONS
export const signInWithGoogle = async () => {
  if (!auth) {
    throw new Error("Authentication service is unavailable. Please check configuration.");
  }
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return { user: result.user, type: 'firebase' };
  } catch (error: any) {
    if (error.code === 'auth/popup-closed-by-user') {
        throw new Error("Sign-in cancelled.");
    }
    if (error.code === 'auth/network-request-failed') {
        throw new Error("Network error. Check your connection or firewall.");
    }
    throw error;
  }
};

export const logout = async () => {
  if (!auth) return;
  try {
    await signOut(auth);
  } catch {
    // Silent fail for logout
  }
};

export interface CreateUserPayload {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  jobTitle: string;
  role: string;
  /** The title's Social Media column (Admin Settings); the function mirrors it to userRoles. */
  canViewSocialMedia?: boolean;
  hourlyRate?: number;
  avatarUrl?: string;
  sendInvite?: boolean;
}

export interface CreateUserResult {
  success: boolean;
  userId: string;
  email: string;
  emailSent: boolean;
}

// Callable function — the server derives the caller's identity from the verified
// auth token (request.auth.uid), so no callerUid travels in the payload. Errors
// reject with FirebaseError codes like "functions/permission-denied", matching
// the shape the old relay implementation produced.
export const callCreateUser = async (payload: CreateUserPayload): Promise<CreateUserResult> => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw { code: "functions/unauthenticated", message: "You must be logged in. Please refresh." };

  // Strip undefined values (e.g. tempPassword is undefined for Google auth users)
  const cleanPayload = Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined)
  );
  const createUserAccount = httpsCallable(functions, "createUserAccount");
  const result = await createUserAccount(cleanPayload);
  return result.data as CreateUserResult;
};

export interface CreateNotificationPayload {
  type: 'mention' | 'workflow' | 'system' | 'urgent';
  recipientUserId: string;
  message: string;
  relatedJobId?: string;
  relatedActivityId?: string;
  body?: string;
}

// Route all notification creates through the Cloud Function (H-1 fix).
// Firestore rules set allow create: if false on /notifications so clients can't bypass this.
export const callCreateNotification = async (payload: CreateNotificationPayload): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw { code: "functions/unauthenticated", message: "You must be logged in. Please refresh." };
  const cleanPayload = Object.fromEntries(
    Object.entries(payload).filter(([, v]) => v !== undefined && v !== '')
  );
  const fn = httpsCallable(functions, "createNotification");
  await fn(cleanPayload);
};

/** Office: a one-hour signed link to a finished inspection download. The browser downloads it natively. */
export const callGetInspectionExportLink = async (inspectionId: string, exportId: string): Promise<string> => {
  if (!auth.currentUser) throw { code: "functions/unauthenticated", message: "You must be logged in. Please refresh." };
  const fn = httpsCallable(functions, "getInspectionExportLink");
  const res = await fn({ inspectionId, exportId });
  return (res.data as { url: string }).url;
};

export const callGenerateWeeklySummary = async (weekStart?: string): Promise<{ ok: boolean; weekStart: string }> => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw { code: "functions/unauthenticated", message: "You must be logged in. Please refresh." };

  const requestId = `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  return new Promise<{ ok: boolean; weekStart: string }>((resolve, reject) => {
    // Generation queries several collections + calls Gemini — allow up to 3 minutes.
    const timeout = setTimeout(() => {
      unsubscribe();
      deleteDoc(doc(db, "functionRequests", requestId)).catch(() => {});
      reject({ code: "functions/deadline-exceeded", message: "Request timed out after 180 seconds." });
    }, 180000);

    const unsubscribe = onSnapshot(
      doc(db, "functionResults", requestId),
      (snap) => {
        if (!snap.exists()) return;
        clearTimeout(timeout);
        unsubscribe();
        const data = snap.data()!;
        deleteDoc(doc(db, "functionResults", requestId)).catch(() => {});
        if (data["error"]) {
          reject({ code: `functions/${data["error"]}`, message: data["message"] || "Request failed" });
        } else {
          resolve(data as { ok: boolean; weekStart: string });
        }
      },
      (error) => {
        clearTimeout(timeout);
        unsubscribe();
        reject({ code: "functions/internal", message: error.message });
      }
    );

    const payload: Record<string, string> = {
      type: "generateWeeklySummary",
      callerUid: currentUser.uid,
      requestedAt: new Date().toISOString(),
    };
    if (weekStart) payload.weekStart = weekStart;
    setDoc(doc(db, "functionRequests", requestId), payload).catch((err) => {
      clearTimeout(timeout);
      unsubscribe();
      reject({ code: "functions/internal", message: err.message });
    });
  });
};

// ─── QBO Firestore-based helpers (mirrors callCreateUser pattern) ─────────────

function qboRequest<T>(type: string, extra?: Record<string, unknown>, timeoutMs = 30000): Promise<T> {
  const currentUser = auth.currentUser;
  if (!currentUser) throw { code: "functions/unauthenticated", message: "You must be logged in. Please refresh." };

  const requestId = `qbo_${type}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  return new Promise<T>((resolve, reject) => {
    const timeout = setTimeout(() => {
      unsubscribe();
      deleteDoc(doc(db, "functionRequests", requestId)).catch(() => {});
      reject({ message: `Request timed out after ${timeoutMs / 1000} seconds.` });
    }, timeoutMs);

    const unsubscribe = onSnapshot(
      doc(db, "functionResults", requestId),
      (snap) => {
        if (!snap.exists()) return;
        clearTimeout(timeout);
        unsubscribe();
        const data = snap.data()!;
        deleteDoc(doc(db, "functionResults", requestId)).catch(() => {});
        if (data["error"]) reject({ message: data["message"] || "Request failed" });
        else resolve(data as unknown as T);
      },
      (error) => {
        clearTimeout(timeout);
        unsubscribe();
        reject({ message: error.message });
      }
    );

    setDoc(doc(db, "functionRequests", requestId), {
      type,
      callerUid: currentUser.uid,
      requestedAt: new Date().toISOString(),
      ...extra,
    }).catch((err) => {
      clearTimeout(timeout);
      unsubscribe();
      reject({ message: err.message });
    });
  });
}

export const callInitiateQBOAuth = (): Promise<{ authUrl: string }> =>
  qboRequest<{ authUrl: string }>("qboInitiate");

export const callExchangeQBOCode = (code: string, state: string, realmId: string): Promise<{ success: boolean }> =>
  qboRequest<{ success: boolean }>("qboExchange", { code, state, realmId });

export const callSyncQBOInvoices = (): Promise<{ imported: number; skipped: number }> =>
  // syncQBOInvoices is configured for timeoutSeconds: 300 — the client must wait longer
  // than the server can run, or a slow-but-successful sync reports a false failure.
  qboRequest<{ imported: number; skipped: number }>("qboSync", undefined, 320000);

type EstimateBase = { walkId: string; target: string };
// The function allows 60s; the client waits a little longer so a slow-but-successful send never reports failure.
const ESTIMATE_TIMEOUT_MS = 70000;

export function callBidWalkQboEstimate(p: EstimateBase & { mode: 'preview' }): Promise<BidWalkEstimatePreviewResult>;
export function callBidWalkQboEstimate(p: EstimateBase & { mode: 'listCustomers' }): Promise<{ customers: Array<{ id: string; name: string }> }>;
export function callBidWalkQboEstimate(p: EstimateBase & {
  mode: 'send'; customerId: string; customerName: string; footer: string; depositNote: boolean; expectedTotal: number;
}): Promise<{ saved: BidWalkQboEstimate }>;
export function callBidWalkQboEstimate(p: EstimateBase & Record<string, unknown>): Promise<unknown> {
  return qboRequest<unknown>("bidWalkQboEstimate", p, ESTIMATE_TIMEOUT_MS);
}

type InvoiceBase = { jobId: string; group: number };

export function callJobQboInvoice(p: InvoiceBase & { mode: 'preview' }): Promise<JobInvoicePreviewResult>;
export function callJobQboInvoice(p: InvoiceBase & { mode: 'listCustomers' }): Promise<{ customers: Array<{ id: string; name: string }> }>;
export function callJobQboInvoice(p: InvoiceBase & { mode: 'listItems' }): Promise<{ items: QboItemOption[] }>;
export function callJobQboInvoice(p: InvoiceBase & {
  mode: 'send'; noteId: string; customerId: string; lines: Array<{ itemId: string; description: string; amount: number }>;
}): Promise<{ saved: JobQboInvoice }>;
export function callJobQboInvoice(p: InvoiceBase & Record<string, unknown>): Promise<unknown> {
  // Same 60s function limit as estimates, so the same client wait.
  return qboRequest<unknown>("jobQboInvoice", p, ESTIMATE_TIMEOUT_MS);
}

type ApprovalLine = { itemId: string; description: string; amount: number };

/** Owner approval before QuickBooks: ask for approval, approve, send back, or withdraw. */
export function callQboApproval(p: {
  mode: 'request'; kind: 'invoice'; jobId: string; group: number; noteId: string; customerId: string; customerName: string; lines: ApprovalLine[];
}): Promise<{ approval: QboApproval }>;
export function callQboApproval(p: {
  mode: 'request'; kind: 'estimate'; walkId: string; target: string; customerId: string; customerName: string; footer: string; depositNote: boolean;
}): Promise<{ approval: QboApproval }>;
// requestedAt: the request the owner is looking at. The function refuses the answer if the request was
// pulled back and sent again since (an answer queued offline must not land on the newer one).
export function callQboApproval(p: {
  mode: 'approve'; approvalId: string; requestedAt: string; lines?: ApprovalLine[]; footer?: string; depositNote?: boolean; expectedTotal?: number;
}): Promise<{ approval: QboApproval }>;
export function callQboApproval(p: {
  mode: 'sendBack'; approvalId: string; requestedAt: string; note: string; lines?: ApprovalLine[]; footer?: string; depositNote?: boolean;
}): Promise<{ approval: QboApproval }>;
export function callQboApproval(p: { mode: 'withdraw'; approvalId: string }): Promise<{ approval: QboApproval }>;
export function callQboApproval(p: Record<string, unknown>): Promise<unknown> {
  // Approving sends to QuickBooks, so it gets the same client wait as the other QuickBooks relays.
  return qboRequest<unknown>("qboApproval", p, ESTIMATE_TIMEOUT_MS);
}

// Fire-and-forget: just writes the request and returns. The function runs in
// the background and updates integrations/quickbooks with historicalImportRunning
// and historicalImportDone — the UI watches those fields via onSnapshot.
export const callSyncQBOInvoicesHistorical = async (startDate?: string): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw { code: "functions/unauthenticated", message: "You must be logged in. Please refresh." };
  const requestId = `qbo_qboHistoricalSync_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  await setDoc(doc(db, "functionRequests", requestId), {
    type: "qboHistoricalSync",
    callerUid: currentUser.uid,
    requestedAt: new Date().toISOString(),
    ...(startDate ? { startDate } : {}),
  });
};

/** The unit-by-unit AI runs a Cloud Function drives over a bid walk. Each has a request type and a run field on the walk. */
export type BidWalkRunType = "bidWalkCleanup" | "bidWalkQb";

/**
 * Ask a Cloud Function to start (or resume) a unit-by-unit AI run over a bid walk:
 * `runBidWalkCleanup` (v6.27.0) or `runBidWalkQbDescriptions` (v6.28.0).
 * Fire-and-forget: the function drives the run and reports through the walk's run
 * field (`cleanupRun` / `qbRun`), which the screen watches via the walk listener — so
 * the run survives the app being closed. The caller writes that field (status
 * `queued`, the same `runId`) to the walk first, or the function has nothing to pick up.
 */
export const requestBidWalkRun = async (type: BidWalkRunType, walkId: string, runId: string): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw { code: "functions/unauthenticated", message: "You must be logged in. Please refresh." };
  const requestId = `${type}_${runId}_${Date.now()}`;
  await setDoc(doc(db, "functionRequests", requestId), {
    type,
    callerUid: currentUser.uid,
    requestedAt: new Date().toISOString(),
    walkId,
    runId,
  });
};

/**
 * Manually trigger Phase 2 AI classification (fire-and-forget).
 * Used if Phase 2 failed and needs to be retried independently of Phase 1.
 */
export const callClassifyQBOInvoices = async (): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) throw { code: "functions/unauthenticated", message: "You must be logged in. Please refresh." };
  const requestId = `qbo_qboClassify_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  await setDoc(doc(db, "functionRequests", requestId), {
    type: "qboClassify",
    callerUid: currentUser.uid,
    requestedAt: new Date().toISOString(),
  });
};

/**
 * Reset a stuck historical import — clears historicalImportRunning/Error/Progress
 * so the admin can trigger a fresh import. Admin-only Firestore update.
 */
export const resetHistoricalImport = async (): Promise<void> => {
  await updateDoc(doc(db, 'integrations', 'quickbooks'), {
    historicalImportRunning: false,
    historicalRawImportDone: false,
    historicalAIRunning: false,
    historicalImportError: deleteField(),
    historicalImportProgress: deleteField(),
  });
};

export { auth, app, db, storage, functions, appCheck };